import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from "react";
import {
  executeApplicationCallbackAction,
  listApplicationCallbacks,
  saveApplicationCallback,
  type Application,
  type ApplicationCallbackConfiguration,
  type ApplicationCallbackType,
} from "../api";
import { ManagementHeading, TimeValue } from "./ManagementCommon";
import { Button } from "./ui/Button";
import { CopyField } from "./ui/CopyField";
import { Icon, type IconName } from "./ui/Icon";

const callbackTypes: readonly ApplicationCallbackType[] = [
  "MESSAGE_BEFORE_SEND",
  "MESSAGE_AFTER_SEND",
  "USER_ONLINE",
  "USER_OFFLINE",
];

interface CallbackMetadata {
  readonly title: string;
  readonly description: string;
  readonly icon: IconName;
}

const metadata: Record<ApplicationCallbackType, CallbackMetadata> = {
  MESSAGE_BEFORE_SEND: {
    title: "消息发送前",
    description: "同步审核消息；失败或超时会阻断发送",
    icon: "warning",
  },
  MESSAGE_AFTER_SEND: {
    title: "消息发送后",
    description: "消息可靠接受后异步、至少一次投递",
    icon: "activity",
  },
  USER_ONLINE: {
    title: "用户上线",
    description: "用户有效设备数从 0 变为 1 时触发",
    icon: "users",
  },
  USER_OFFLINE: {
    title: "用户下线",
    description: "最后设备离线并超过重连宽限期后触发",
    icon: "logout",
  },
};

export function CallbackConfigurationView({ application, applications, onSelect, onNotice, onError }: {
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly onSelect: (application: Application) => void;
  readonly onNotice: (message: string) => void;
  readonly onError: (message: string) => void;
}) {
  const [callbacks, setCallbacks] = useState<readonly ApplicationCallbackConfiguration[]>([]);
  const [selectedType, setSelectedType] = useState<ApplicationCallbackType>("MESSAGE_BEFORE_SEND");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const selected = useMemo(
    () => callbacks.find((callback) => callback.callbackType === selectedType) ?? null,
    [callbacks, selectedType],
  );
  const reload = useCallback(() => { setReloadKey((value) => value + 1); }, []);

  useEffect(() => {
    setCallbacks([]);
    setSecret(null);
    setSelectedType("MESSAGE_BEFORE_SEND");
  }, [application?.appId]);

  useEffect(() => {
    if (application === null) return;
    let active = true;
    setLoading(true);
    void listApplicationCallbacks(application.appId)
      .then((items) => { if (active) setCallbacks(items); })
      .catch((cause: unknown) => { if (active) onError(errorMessage(cause)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [application?.appId, onError, reloadKey]);

  async function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    if (application === null) return;
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setSecret(null);
    try {
      const result = await saveApplicationCallback(application.appId, selectedType, {
        callbackUrl: formString(data, "callbackUrl").trim(),
        timeoutMs: Number(data.get("timeoutMs")),
        maxAttempts: selectedType === "MESSAGE_BEFORE_SEND" ? 1 : Number(data.get("maxAttempts")),
        subscribedConversationTypes: formStrings(data, "conversationTypes"),
        subscribedMessageTypes: formStrings(data, "messageTypes"),
        subscribedMessageSources: formStrings(data, "messageSources"),
        ...(selected === null ? {} : { version: selected.version }),
      });
      if (result.secret !== undefined) setSecret(result.secret);
      onNotice(selected === null
        ? "回调配置已创建，请立即保存一次性密钥"
        : "回调配置已保存，需重新验证后启用");
      reload();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function action(value: "enable" | "disable" | "verify" | "rotate-secret"): Promise<void> {
    if (application === null || selected === null) return;
    setBusy(true);
    setSecret(null);
    try {
      const result = await executeApplicationCallbackAction(
        application.appId,
        selected.callbackType,
        value,
      );
      if (result.secret !== undefined) setSecret(result.secret);
      onNotice(value === "verify"
        ? "测试事件验证成功"
        : value === "rotate-secret"
          ? "密钥已轮换，请立即保存"
          : value === "enable"
            ? "回调已启用"
            : "回调已停用");
      reload();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const selectedMetadata = metadata[selectedType];
  const selectedState = callbackState(selected);

  return (
    <div className="management-page callback-page">
      <ManagementHeading
        title="回调配置"
        description="按应用配置同步消息审核、可靠消息通知和聚合用户状态事件。"
        applications={applications}
        selected={application}
        onSelect={onSelect}
      />
      {application === null ? (
        <div className="empty-page"><strong>请先创建应用</strong><p>回调配置严格归属于单个应用。</p></div>
      ) : (
        <div className="callback-workspace">
          <section className="callback-overview-grid" aria-label="回调类型">
            {callbackTypes.map((type) => {
              const callback = callbacks.find((item) => item.callbackType === type);
              const state = callbackState(callback ?? null);
              const item = metadata[type];
              const isSelected = selectedType === type;
              return (
                <button
                  aria-pressed={isSelected}
                  className={`callback-type-card ${isSelected ? "selected" : ""}`}
                  key={type}
                  type="button"
                  onClick={() => { setSelectedType(type); setSecret(null); }}
                >
                  <span className="callback-type-card-header">
                    <span className="callback-type-icon"><Icon name={item.icon} size={17} /></span>
                    <span className={`management-tag ${state.className}`}>{state.label}</span>
                  </span>
                  <span className="callback-type-copy">
                    <strong>{item.title}</strong>
                    <small>{item.description}</small>
                  </span>
                  <span className="callback-type-code">
                    <code>{type}</code><Icon name="chevron-right" size={15} />
                  </span>
                </button>
              );
            })}
          </section>

          {secret === null ? null : (
            <section className="surface-card callback-secret-panel" role="status">
              <div className="callback-secret-copy">
                <span className="callback-secret-icon"><Icon name="key" size={18} /></span>
                <div><h3>一次性回调密钥</h3><p>关闭后不再显示，请仅保存于业务服务端 Secret 管理系统。</p></div>
              </div>
              <CopyField label="Callback Secret" sensitive value={secret} />
            </section>
          )}

          <section className="data-card callback-editor">
            <header className="data-card-header callback-editor-header">
              <div className="callback-editor-title">
                <span><Icon name={selectedMetadata.icon} size={18} /></span>
                <div><h3>{selectedMetadata.title}</h3><p>{selectedMetadata.description}</p></div>
              </div>
              <div className="callback-status-meta">
                <span className={`management-tag ${selectedState.className}`}>{selectedState.label}</span>
                {selected === null ? null : (
                  <span className="callback-verification-time">
                    <small>最近验证</small><TimeValue value={selected.lastVerifiedAt} />
                  </span>
                )}
              </div>
            </header>

            {loading ? (
              <div className="management-state"><span className="loader" /><strong>正在加载回调配置</strong></div>
            ) : (
              <form
                key={`${selectedType}:${selected?.version ?? 0}`}
                className="callback-form"
                onSubmit={(event) => { void submit(event); }}
              >
                <section className="callback-form-section">
                  <header className="callback-form-section-heading">
                    <div><h4>投递端点</h4><p>配置接收地址、单次超时和失败重试边界。</p></div>
                    <code>HTTPS</code>
                  </header>
                  <div className="field-grid callback-endpoint-grid">
                    <label className="field field-wide">
                      <span>HTTPS 回调地址</span>
                      <input
                        name="callbackUrl"
                        type="url"
                        required
                        maxLength={2048}
                        placeholder="https://api.example.com/im/callback"
                        defaultValue={selected?.callbackUrl ?? ""}
                      />
                      <small>禁止内网、本机、元数据地址和重定向；保存和投递时都会校验目标。</small>
                    </label>
                    <label className="field">
                      <span>超时（毫秒）</span>
                      <input
                        name="timeoutMs"
                        type="number"
                        min={100}
                        max={3000}
                        defaultValue={selected?.timeoutMs ?? (selectedType === "MESSAGE_BEFORE_SEND" ? 1000 : 3000)}
                      />
                      <small>允许范围 100–3000 ms</small>
                    </label>
                    <label className="field">
                      <span>最大投递次数</span>
                      <input
                        name="maxAttempts"
                        type="number"
                        min={1}
                        max={8}
                        disabled={selectedType === "MESSAGE_BEFORE_SEND"}
                        defaultValue={selectedType === "MESSAGE_BEFORE_SEND" ? 1 : selected?.maxAttempts ?? 8}
                      />
                      <small>前置回调固定为 1；异步回调最多 8 次</small>
                    </label>
                  </div>
                </section>

                {selectedType.startsWith("MESSAGE_") ? (
                  <section className="callback-form-section">
                    <header className="callback-form-section-heading">
                      <div><h4>事件过滤</h4><p>缩小回调范围；未勾选的维度表示接收全部。</p></div>
                    </header>
                    <div className="callback-subscriptions">
                      <OptionGroup
                        name="conversationTypes"
                        title="会话类型"
                        values={["C2C", "GROUP", "ROOM", "CHANNEL", "BROADCAST"]}
                        selected={selected?.subscribedConversationTypes ?? []}
                      />
                      <OptionGroup
                        name="messageTypes"
                        title="消息类型"
                        values={["TEXT", "IMAGE", "AUDIO", "VIDEO", "FILE", "CUSTOM", "COMMAND", "SYSTEM", "EDIT", "RETRACT"]}
                        selected={selected?.subscribedMessageTypes ?? []}
                      />
                      <OptionGroup
                        name="messageSources"
                        title="消息来源"
                        values={["CLIENT", "SERVER_API"]}
                        selected={selected?.subscribedMessageSources ?? []}
                      />
                    </div>
                  </section>
                ) : null}

                {selectedType === "MESSAGE_BEFORE_SEND" ? (
                  <div className="management-security-note callback-warning">
                    <Icon name="warning" size={16} />
                    <span><strong>失败关闭：</strong>启用后，超时、非 2xx、非法响应、并发饱和或业务拒绝都会阻断消息，且不会分配 seq。</span>
                  </div>
                ) : null}

                <footer className="callback-actions">
                  <Button loading={busy} type="submit" variant="primary">保存配置</Button>
                  {selected === null ? null : (
                    <div className="callback-secondary-actions">
                      <Button disabled={busy} type="button" onClick={() => { void action("verify"); }}>发送测试事件</Button>
                      <Button disabled={busy} type="button" onClick={() => { void action(selected.enabled ? "disable" : "enable"); }}>
                        {selected.enabled ? "停用" : "启用"}
                      </Button>
                      <Button
                        disabled={busy}
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          if (window.confirm("轮换后请在重叠窗口内更新业务服务端密钥。确认继续？")) {
                            void action("rotate-secret");
                          }
                        }}
                      >轮换密钥</Button>
                    </div>
                  )}
                </footer>
              </form>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function callbackState(callback: ApplicationCallbackConfiguration | null | undefined): {
  readonly label: string;
  readonly className: "tag-success" | "tag-info" | "tag-muted" | "tag-danger";
} {
  if (callback === null || callback === undefined) return { label: "未配置", className: "tag-muted" };
  if (callback.enabled) return { label: "已启用", className: "tag-success" };
  if (callback.status === "VERIFIED") return { label: "已验证", className: "tag-info" };
  if (callback.status === "ERROR") return { label: "异常", className: "tag-danger" };
  if (callback.status === "DISABLED") return { label: "已停用", className: "tag-muted" };
  return { label: "待验证", className: "tag-muted" };
}

function formString(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

function formStrings(data: FormData, name: string): readonly string[] {
  return data.getAll(name).filter((value): value is string => typeof value === "string");
}

function OptionGroup({ name, title, values, selected }: {
  readonly name: string;
  readonly title: string;
  readonly values: readonly string[];
  readonly selected: readonly string[];
}) {
  return (
    <fieldset>
      <legend><span>{title}</span><small>不选择表示全部</small></legend>
      <div className="callback-option-list">
        {values.map((value) => (
          <label key={value}>
            <input type="checkbox" name={name} value={value} defaultChecked={selected.includes(value)} />
            <span>{value}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
